import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Typography,
} from "@mui/material";
import React, { FormEvent, useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import LoginWithUserIdandPasswordStyles from "../LoginWithUserIdandPassword/LoginWithUserIdandPasswordStyles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { agentLogin } from "../agentAuthActions";
import { IAgentLoginRequestBody } from "../../../models/agent/agentAuth/agentAuth";
import { t } from "i18next";

const LoginWithUserIdandPassword = () => {
  const dispatch = useAppDispatch();

  const [agentLoginRequestBody, setAgentLoginRequestBody] =
    useState<IAgentLoginRequestBody>({
      userId: "",
      password: "",
      loginTime: "",
    });

  const { isLoading, calledReducerType, isError, message } = useAppSelector(
    (state) => state.common
  );

  const [validationErrors, setValidationErrors] = useState<{
    userId?: string;
    password?: string;
  }>({
    userId: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleClickShowPassword = () =>
    setShowPassword((prevState) => !prevState);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;
      setValidationErrors((prevState) => ({ ...prevState, [name]: "" }));

      setAgentLoginRequestBody((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const validate = (): { userId: string; password: string } => {
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    const errors = {
      userId: "",
      password: "",
    };

    if (
      !agentLoginRequestBody.userId ||
      agentLoginRequestBody.userId.trim().length === 0
    ) {
      errors.userId = "User Id cannot be blank";
    }

    if (
      !agentLoginRequestBody.password ||
      agentLoginRequestBody.password.trim().length === 0
    ) {
      errors.password = "Password cannot be blank";
    }

    return errors;
  };

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const errors = validate();
      if (Object.values(errors).some((error) => error)) {
        setValidationErrors(errors);
        return;
      }
      const currentLoginTime = new Date().toString();
      dispatch(
        agentLogin({ ...agentLoginRequestBody, loginTime: currentLoginTime })
      );
    },
    [agentLoginRequestBody, dispatch, validate]
  );

  return (
    <form
      onSubmit={handleSubmit}
      style={LoginWithUserIdandPasswordStyles.loginWithNameAndMobileForm}
    >
      {isError && calledReducerType === "agentAuth/agentLogin" ? (
        <Alert severity="error">
          {message || "Something went wrong, please try again..."}
        </Alert>
      ) : (
        <></>
      )}
      <FormControl fullWidth>
        <InputLabel>User Id</InputLabel>
        <OutlinedInput
          id="loginpage_userid-input"
          data-testid="test-loginpage_userid-input"
          aria-label="Enter UserId"
          label="User Id"
          type="text"
          placeholder="Please enter your user id"
          name="userId"
          onChange={handleChange}
          value={agentLoginRequestBody.userId}
          error={Boolean(validationErrors.userId)}
          autoComplete="user-id"
        />
        {validationErrors.userId ? (
          <Typography color="error">{validationErrors.userId}</Typography>
        ) : (
          <></>
        )}
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Password</InputLabel>
        <OutlinedInput
          id="loginpage_password-input"
          data-testid="test-loginpage_password-input"
          aria-label="Enter Password"
          label="Password"
          type={showPassword ? "text" : "password"}
          placeholder="Please enter your password"
          name="password"
          onChange={handleChange}
          value={agentLoginRequestBody.password}
          error={Boolean(validationErrors.password)}
          autoComplete="new-password"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        {validationErrors.password ? (
          <Typography color="error">{validationErrors.password}</Typography>
        ) : (
          <></>
        )}
      </FormControl>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Link
          href="#"
          underline="hover"
          id="loginpage_link-forgot-password"
          data-testid="test-loginpage_link-forgot-password"
          aria-label="Navigate to forgot-password"
        >
          Forgot Password?
        </Link>
      </Box>
      <Button
        id="loginpage_button-login"
        data-testid="test-loginpage_button-login"
        aria-label="Click to perform login"
        variant="contained"
        type="submit"
        disabled={Boolean(
          validationErrors.userId || Boolean(validationErrors.password)
        )}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {isLoading && calledReducerType === "agentAuth/agentLogin" && (
          <CircularProgress color="info" size="20px" />
        )}
        {"Login"}
      </Button>
    </form>
  );
};

export default LoginWithUserIdandPassword;
